'use client';

import type { Advisor } from '@ria-client/advisor-search/types';
import { useNWAElementImpression } from '@ria-client/segment/hooks/useNWAElementImpression';
import { useStandardTrack } from '@ria-client/segment/hooks/useStandardTrack';
import { useEffect, useId, useMemo, useRef } from 'react';

import { MatchCtaLink } from '../MatchCtaLink';
import { DetailsToggleButton } from './DetailsToggleButton';
import { IapdLinkButton } from './IapdLinkButton';
import { StatTile } from './StatTile';
import { TagChip } from './TagChip';
import { formatAddressLine, getExpandedStats, getHeaderStats } from './utils';

const IAPD_LINK_COPY = 'View on SEC IAPD';

export interface AdvisorCardProps {
  advisor: Advisor;
  expanded: boolean;
  onToggle: () => void;
  position: number;
}

export function AdvisorCard({ advisor, expanded, onToggle, position }: AdvisorCardProps) {
  const track = useStandardTrack();
  const firmId = useId();
  const panelId = useId();
  const iapdDestinationUrl = `https://adviserinfo.sec.gov/firm/summary/${advisor.crd}`;
  const {
    ref: cardRef,
    impressionId: cardViewEventId,
    ensureViewed: ensureCardViewed,
  } = useNWAElementImpression({ component: 'advisor_card' });
  // The event must rotate when a recycled card receives a different advisor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const iapdViewEventId = useMemo(() => crypto.randomUUID(), [advisor.crd]);
  const iapdViewFiredForRef = useRef<string | null>(null);
  const specialties = [...advisor.services, ...advisor.clientTypes];

  useEffect(() => {
    if (!expanded || iapdViewFiredForRef.current === advisor.crd) return;
    iapdViewFiredForRef.current = advisor.crd;
    void track('Link Viewed', {
      entity_name: 'advisor_iapd_link',
      destination_url: iapdDestinationUrl,
      event_id: iapdViewEventId,
      link_copy: IAPD_LINK_COPY,
      additional_attributes: { crd: advisor.crd, firm_name: advisor.firm, position },
    });
  }, [expanded, track, advisor.crd, advisor.firm, iapdDestinationUrl, iapdViewEventId, position]);

  const handleToggle = () => {
    if (!expanded) {
      ensureCardViewed();
      void track('Element Interacted', {
        entity_name: 'advisor_card',
        interaction_type: 'expanded',
        parent_event_id: cardViewEventId,
        additional_attributes: { crd: advisor.crd, firm_name: advisor.firm, position },
      });
    }
    onToggle();
  };

  const handleIapdClick = () => {
    void track('Link Clicked', {
      entity_name: 'advisor_iapd_link',
      parent_event_id: iapdViewEventId,
      destination_url: iapdDestinationUrl,
      link_copy: IAPD_LINK_COPY,
      additional_attributes: { crd: advisor.crd, firm_name: advisor.firm },
    });
  };

  return (
    <article
      ref={cardRef}
      aria-labelledby={firmId}
      className={`overflow-hidden rounded-callout border bg-card hover:border-[var(--content-green-light)] ${
        expanded
          ? 'border-[var(--content-green-lighter)] shadow-[0_4px_24px_color-mix(in_srgb,var(--content-green-main)_8%,transparent)]'
          : 'border-border'
      }`}
    >
      <div className="p-2 sm:p-4">
        <div className="mb-5">
          <h3 id={firmId} className="typography-text-bold mb-0.5">
            {advisor.firm}
          </h3>
          <p className="typography-text-small text-[var(--content-neutral-dark)]">
            {formatAddressLine(advisor)}
          </p>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {getHeaderStats(advisor).map(stat => (
            <StatTile key={stat.label} {...stat} centered />
          ))}
        </div>

        <div className="mb-5">
          <span className="typography-text-extra-small mb-1 block text-[var(--content-neutral-dark)]">
            Specialties
          </span>
          <div className="flex flex-wrap gap-1.5">
            {specialties.map((specialty, index) => (
              <TagChip key={`${index}:${specialty}`} label={specialty} />
            ))}
          </div>
        </div>

        <MatchCtaLink
          label="Get Matched Now"
          actionSection="card_cta"
          crd={advisor.crd}
          fullWidth
          showChevron
          uppercase
          className="h-[45px] py-0"
        />

        <div className="mt-2 flex justify-center">
          <DetailsToggleButton expanded={expanded} onClick={handleToggle} controlsId={panelId} />
        </div>
      </div>

      {expanded ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={firmId}
          className="border-t border-dashed border-border px-4 pt-5 pb-4"
        >
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
            {getExpandedStats(advisor).map(stat => (
              <StatTile key={stat.label} {...stat} centered />
            ))}
          </div>

          <div className="mb-4">
            <span className="typography-text-extra-small mb-1 block text-[var(--content-neutral-dark)]">
              Fee Schedule
            </span>
            <div className="flex flex-wrap gap-1">
              {advisor.fees.aum ? <TagChip variant="fee" label="AUM-Based" /> : null}
              {advisor.fees.flatFee ? <TagChip variant="fee" label="Flat Fee" /> : null}
              {advisor.fees.hourly ? <TagChip variant="fee" label="Hourly" /> : null}
              {advisor.fees.commission ? <TagChip variant="fee" label="Commission" /> : null}
              {advisor.fees.performance ? (
                <TagChip variant="fee" label="Performance-Based" />
              ) : null}
              {advisor.fees.otherText ? (
                <TagChip variant="fee" label={`Other: ${advisor.fees.otherText}`} />
              ) : null}
            </div>
          </div>

          {advisor.otherServices ? (
            <div className="mb-4">
              <span className="typography-text-extra-small mb-1 block text-[var(--content-neutral-dark)]">
                Other Services
              </span>
              <TagChip label={advisor.otherServices} />
            </div>
          ) : null}

          <IapdLinkButton crd={advisor.crd} onClick={handleIapdClick} />
        </div>
      ) : null}
    </article>
  );
}
